import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { db } from '../../firebase';
import { PDFExport } from '@progress/kendo-react-pdf';
import Breadcrumbs from '../../components/breadcrumbs';
import { Link } from 'react-router-dom';
import { average, relationshipColor, convertRelationships, difference, convertRelationshipTextToKey, valueCheck, percentageCheck, arrayGenerator, getChartOptions, collectRadarCaptionsData} from '../../helpers';
import RadarChart from '../../components/charts/radarchart';
import SiteHeader from '../../components/site-header';
import LoadingState from '../../components/global/loading-state';
import withAuthorisation from '../../components/withauthorisation';
import ReportPageTemplate from './reportpagetemplate.jsx';
import ComparisonTable from '../../components/reports/ComparisonTable';
import DetailedFeedbackSection from '../../components/reports/detailedFeedbackSection';
import RelationshipCompetencyComparison from '../../components/charts/RelationshipCompetencyComparison';
import RankedCompetencies from '../../components/reports/RankedCompetencies';
import OrderedStatements from '../../components/reports/OrderedStatements';
import StatementVariations from '../../components/reports/StatementVariations';
import PDFExportButton from '../../components/reports/PDFExportButton';

const authCondition = (authUser) => !!authUser;

class UserComparisonReport extends Component {
  constructor(props) {
    super(props);
    this.exportPDF = this.exportPDF.bind(this);
    this.convertSVGToImage = this.convertSVGToImage.bind(this);
    this.updateCompetencyComparisonPDFExportPreRenderStatus = this.updateCompetencyComparisonPDFExportPreRenderStatus.bind(this);
    this.updateRankedCompetenciesPDFExportPreRenderStatus = this.updateRankedCompetenciesPDFExportPreRenderStatus.bind(this);
    this.updateOrderedStatementsPDFExportPreRenderStatus = this.updateOrderedStatementsPDFExportPreRenderStatus.bind(this);
    this.updateStatementVariationsPDFExportPreRenderStatus = this.updateStatementVariationsPDFExportPreRenderStatus.bind(this);
    this.updateDetailedFeedbackPDFExportPreRenderStatus = this.updateDetailedFeedbackPDFExportPreRenderStatus.bind(this);
    this.state = {
      loading: true,
      current360Groups: this.props.current360Groups,
      current360Relationships: this.props.current360Relationships,
      sectionsLegacyMode: false,
      pdfRequested: false,
      competencyComparisonPDFRenderCompleted: false,
      rankedCompetenciesPDFRenderCompleted: false,
      orderedStatementsPDFRenderCompleted: false,
      statementVariationsPDFRenderCompleted: false,
      detailedFeedbackPDFRenderCompleted: false,
      layoutMode: 'default'
    };
  }

  exportPDF = () => {
    if (this.state.layoutMode !== 'pdfCompleted') {
      // triggers re-render process to split each question into pages that account for overflow
      this.setState({pdfRequested: true, layoutMode: 'pdfRecalculate'})
    }
  };

  convertSVGToImage = (htmlString) => {
    // Take an SVG html string, convert it to static markup
    let staticRadarChart = ReactDOMServer.renderToStaticMarkup(htmlString);

    // Convert SVG string into a data URI
    const svgDataUri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(staticRadarChart)))}`;

    return svgDataUri;
  };

  componentDidMount() {
    const {
      match: { params },
    } = this.props;
    this.setState({
      userId: params.userId,
      groupId: params.groupId,
      current360Id: params.id,
    });

    db.doGetCurrentGroup(this.props.match.params.accountId, params.id, params.groupId).onSnapshot((snapshot) => {
      if (snapshot.data()) {
        this.setState({
          currentGroup: snapshot.data(),
        });

        db.getUserFromGroup(params.accountId, params.userId).onSnapshot((snapshot) => {
          let userFullName = `${snapshot.data()['forename']} ${snapshot.data()['surname']}`;
          let reportPdfFileName = `${snapshot.data()['forename']}-${snapshot.data()['surname']}-comparison-report.pdf`;

          db.doGetCurrent360(params.accountId, params.id).then((snapshot) => {
            let questionnaireID = snapshot.data()['questionnaireID'];
            let current360Title = snapshot.data()['title'];
            // let current360Relationships = snapshot.data()['relationships']
            let current360Relationships = this.state.current360Relationships;
            let sectionsLegacyMode = false;

            let sortedRelationships = true;
            let orderedRelationshipKeys = [];
            for (const relationshipID in current360Relationships) {
              orderedRelationshipKeys.push(relationshipID)
              const {sortOrder} = current360Relationships[relationshipID];
              if (sortOrder === undefined) {
                sortedRelationships = false;
              }
            }
            // Re-orders orderedRelationshipKeys using sortOrder if present and alphabetically if not
            if (sortedRelationships) {
              orderedRelationshipKeys.sort((a, b) => {
                return current360Relationships[a].sortOrder - current360Relationships[b].sortOrder;
              });
            } else {
              orderedRelationshipKeys.sort();
            }

            db.doGetSectionsAndQuestionDetailsList(params.accountId, questionnaireID).then((sections) => {
              if (
                Object.values(sections)
                  .map((section) => section.sortOrder)
                  .includes(undefined)
              ) {
                sectionsLegacyMode = true;
              }

              let questionnaireSections = sections;
              let captions = {};
              let descriptions = {};
              let questionDetails = {};
              let sectionDetails = questionnaireSections;
              let userAnswers = {};
              let userResponses = {};
              let userCommentsBySection = {};
              let userIndividualAnswers = {};
              let userIndividualResponses = [];
              let freeTextQuestionIDs = [];
              let sectionAnswersAverage = {}
              let relationshipKeys = [];
              let dfSectionRelationshipKeys = [];
              let freeTextQuestionDetails = {};

              Object.keys(questionnaireSections).map((key, index) => {
                let sectionID = key;

                if (questionnaireSections[sectionID].questions) {
                  // Check if only one question AND if that question is a FreeText field!!
                  if (Object.keys(questionnaireSections[sectionID].questions).length == 1) {
                    // MAD:
                    if (questionnaireSections[sectionID].questions[Object.keys(questionnaireSections[sectionID].questions)[0]].answerType === 'FreeText') {
                      // Adds question to freeText questions
                      for (const qid in questionnaireSections[sectionID].questions) {
                        freeTextQuestionDetails[qid] = questionnaireSections[sectionID].questions[qid];
                      }
                      return;
                    }
                  }

                  let hasMultichoice = false;

                  Object.keys(questionnaireSections[sectionID].questions).map((key, index) => {
                    const {answerType} = questionnaireSections[sectionID].questions[key];

                    if (answerType === 'MultiChoice' && !hasMultichoice) {
                      hasMultichoice = true;
                    }

                    // adds any other freetext questions to freeTextQuestionDetails object
                    if (answerType === 'FreeText') {
                      freeTextQuestionDetails[key] = questionnaireSections[sectionID].questions[key];
                    }
                    questionDetails[[key]] = questionnaireSections[sectionID].questions[key];
                  });

                  if (hasMultichoice) {
                    // Set captions + description if section has valid data for radar chart!
                    captions[[sectionID]] = questionnaireSections[sectionID].title;
                    descriptions[[sectionID]] = questionnaireSections[sectionID].description;
                  }

                }
              });
              Object.keys(sectionDetails).map((sectionID, i) => {
                Object.keys(sectionDetails[sectionID].questions).map((questionID, n) => {
                  if (sectionDetails[sectionID].questions[questionID].answerType === 'FreeText') {
                    freeTextQuestionIDs.push(questionID);
                  }
                });
              });
              // This updates state for captions, sectionDetails, radarSortedSectionIDs, numberOfRadarSections, captionKeysList
              const radarCaptionsData = collectRadarCaptionsData(captions, sectionsLegacyMode, sectionDetails);

              // Get answers!
              db.doGetUserAnswersBySections(
                params.accountId,
                params.userId,
                params.id,
                params.groupId,
                questionnaireID,
                'self-review',
                questionnaireSections
              ).then((data) => {
                let questionnaireSections = data;

                Object.keys(questionnaireSections).map((key, index) => {
                  let sectionKey = key;
                  let answers = [];
                  let comments = [];
                  let responses = [0, 0, 0, 0, 0, 0];
                  let answerKey = 0;
                  let multichoiceAnswers = []

                  //// console.log(questionnaireSections);

                  Object.keys(questionnaireSections[sectionKey].answers).map((key, index) => {
                    let answerVal = questionnaireSections[sectionKey].answers[key];
                    // If answer is a number value (IE: not a text field) then set, otherwise set to `0`

                    if (!isNaN(+answerVal)) {
                      multichoiceAnswers.push(+answerVal)
                    }

                    answerVal = isNaN(Number(answerVal)) ? -1 : parseInt(answerVal);

                    if (answerVal == -1) {
                      let userComment = questionnaireSections[sectionKey].answers[key];

                      if (!userCommentsBySection[[sectionKey]]) {
                        userCommentsBySection[[sectionKey]] = {};
                      }
                      if (!userCommentsBySection[[sectionKey]][[key]]) {
                        userCommentsBySection[[sectionKey]][[key]] = {};
                        userCommentsBySection[[sectionKey]][[key]]['title'] = sectionDetails[sectionKey].questions[key].questionTitle;
                      }

                      userCommentsBySection[[sectionKey]][[key]]['comment'] = userComment;
                    } else {
                      if (!questionnaireSections[sectionKey].answers[key].toString() == '') {
                        responses[answerVal] = responses[answerVal] + 1;
                        // user `answerKey` instead of `key` to ensure correct average

                        if (+answerVal > 0) {
                          answers.push(+answerVal)
                        }
                        userIndividualAnswers[key] = answerVal;
                        //// console.log(userIndividualAnswers);

                        answerKey++;
                      }
                    }
                  });

                  if (multichoiceAnswers.length > 0) {
                    sectionAnswersAverage[key] = average(answers)
                  }

                  // // console.log(answers)
                  if (answers.length) {
                    userAnswers[[sectionKey]] = average(answers) / 5;
                  } else {
                    userAnswers[[sectionKey]] = 0;
                  }
                  userResponses[[sectionKey]] = responses;
                });

                // // console.log(userAnswers);

                // Get Reviewer Answers.
                db.doGetCompletedReviewers(params.accountId, params.id, params.groupId, params.userId).then((snapshot) => {
                  if (!snapshot.empty) {
                    let reviewers = {};
                    snapshot.docs.forEach((doc, i) => {
                      reviewers[doc.id] = doc.data();
                      const {relationship} = doc.data();
                      if (!dfSectionRelationshipKeys.includes(relationship)) {
                        dfSectionRelationshipKeys.push(relationship);
                      }
                      if (!relationshipKeys.includes(relationship)) {
                        relationshipKeys.push(relationship)
                      }
                    });
                    // Re-orders relationshipKeys using sortOrder if present and alphabetically if not
                    if (sortedRelationships) {
                      relationshipKeys.sort((a, b) => {
                        return current360Relationships[a].sortOrder - current360Relationships[b].sortOrder;
                      });
                    } else {
                      relationshipKeys.sort();
                    }

                    let numberOfReviewers = Object.keys(reviewers).length;
                    let completedReviewerAnswersAverage = {};
                    let completedReviewerComments = {};
                    let completedReviewerIndividualQuestionsAverage = {};
                    let completedReviewerAnswers = {};

                    Object.keys(current360Relationships).map((key) => {
                      completedReviewerAnswers[key] = {};
                    });

                    let completedReviewerAnswersByRelationship = {};
                    let completedIndividualQuestionsDifferences = {};

                    let completedReviewerResponsesByRelationship = {};

                    let completedReviewerResponses = {};
                    let completedReviewerResponsesAverage = {};

                    let completedReviewerCommentsBySection = {};

                    Object.keys(current360Relationships).map((key) => {
                      completedReviewerResponses[key] = {};
                      completedReviewerResponsesAverage[key] = {};
                    });

                    // Loop through all completed reviewers + split into correct relationship Groups
                    // and then get average of section scores
                    Object.keys(reviewers).map((key, index) => {
                      let loopNum = index + 1;
                      let reviewerRelationship = reviewers[key]['relationship'];

                      db.doGetReviewerAnswers(params.accountId, key, questionnaireSections).then((data) => {
                        let reviewerAnswersSections = data;
                        // // console.log(key);
                        // // console.log(data);
                        let reviewerIDD = key;
                        let answers = [];
                        // // console.log(reviewerAnswersSections);
                        Object.keys(reviewerAnswersSections).map((key, index) => {
                          let sectionKey = key;
                          let answers = [];
                          let comments = [];
                          let responses = [0, 0, 0, 0, 0, 0];

                          // // console.log(reviewerAnswersSections[key]);

                          // Loop through answers in each Section
                          Object.keys(reviewerAnswersSections[key].answers).map((key, index) => {
                            let count = index;
                            count++;

                            let answerVal = reviewerAnswersSections[sectionKey].answers[key];
                            // If answer is a number value (IE: not a text field) then set, otherwise set to `0`
                            // answerVal = isNaN(parseInt(answerVal)) ? -1 : parseInt(answerVal)
                            answerVal = isNaN(Number(answerVal)) ? -1 : parseInt(answerVal);
                            if (+answerVal > 0) {
                              answers.push(+answerVal)
                            }

                            if (answerVal == -1) {
                              comments[index] = reviewerAnswersSections[sectionKey].answers[key];

                              let reviewerComment = reviewerAnswersSections[sectionKey].answers[key];

                              if (!completedReviewerCommentsBySection[[sectionKey]]) {
                                completedReviewerCommentsBySection[[sectionKey]] = {};
                              }
                              if (!completedReviewerCommentsBySection[[sectionKey]][[key]]) {
                                completedReviewerCommentsBySection[[sectionKey]][[key]] = {};
                                completedReviewerCommentsBySection[[sectionKey]][[key]]['title'] = sectionDetails[sectionKey].questions[key].questionTitle;
                                completedReviewerCommentsBySection[[sectionKey]][[key]]['comments'] = [];
                                completedReviewerCommentsBySection[[sectionKey]][[key]]['relationships'] = [];
                                completedReviewerCommentsBySection[[sectionKey]][[key]]['commentsByRelationship'] = {};
                              }

                              if (!completedReviewerCommentsBySection[[sectionKey]][[key]]['commentsByRelationship'][[reviewerRelationship]]) {
                                //// console.log(completedReviewerCommentsBySection[[sectionKey]][[key]]['commentsByRelationship'][[reviewerRelationship]]);
                                completedReviewerCommentsBySection[[sectionKey]][[key]]['commentsByRelationship'][[reviewerRelationship]] = [];
                              }

                              // reviewerRelationship

                              completedReviewerCommentsBySection[[sectionKey]][[key]]['comments'].push(reviewerAnswersSections[sectionKey].answers[key]);
                              completedReviewerCommentsBySection[[sectionKey]][[key]]['commentsByRelationship'][[reviewerRelationship]].push(
                                reviewerAnswersSections[sectionKey].answers[key]
                              );
                              // completedReviewerCommentsBySection[[sectionKey]].push( reviewerAnswersSections[sectionKey].answers[key] )
                            } else {
                              responses[answerVal] = responses[answerVal] + 1;

                              //
                              if (!completedReviewerIndividualQuestionsAverage[[key]]) {
                                completedReviewerIndividualQuestionsAverage[[key]] = [];
                              }
                              completedReviewerIndividualQuestionsAverage[[key]].push(answerVal);

                              if (!isNaN(parseInt(answerVal))) {
                                if (!completedReviewerAnswersByRelationship[key]) {
                                  completedReviewerAnswersByRelationship[key] = {};
                                }

                                if (!completedReviewerAnswersByRelationship[key][reviewerRelationship]) {
                                  completedReviewerAnswersByRelationship[key][reviewerRelationship] = [];
                                }

                                completedReviewerAnswersByRelationship[key][reviewerRelationship].push(answerVal);
                              }
                              

                              // }

                              if (!completedReviewerResponsesByRelationship[[key]]) {
                                completedReviewerResponsesByRelationship[[key]] = [];
                              }

                              if (!completedReviewerResponsesByRelationship[[key]][[reviewerRelationship]]) {
                                completedReviewerResponsesByRelationship[[key]][[reviewerRelationship]] = [];
                              }

                              completedReviewerResponsesByRelationship[[key]][[reviewerRelationship]].push(responses);
                            }
                          });

                          completedReviewerAnswers[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]] = average(answers) / 5;

                          if (completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]]) {
                            Object.keys(completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]]).map((key, index) => {
                              // increase each number
                              completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]][index] =
                                completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]][index] + responses[index];
                            });
                          } else {
                            completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]] = responses;
                          }

                          completedReviewerResponsesAverage[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]] = [];
                          // Loop through
                          Object.keys(completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]]).map((key, index) => {
                            // completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]][key]
                            // listOfAnswers.push()

                            for (let i = 0; i < completedReviewerResponses[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]][key]; i++) {
                              // // console.log(index);
                              if (index != 0) {
                                completedReviewerResponsesAverage[convertRelationshipTextToKey(reviewerRelationship)][[sectionKey]].push(index);
                              }
                            }
                          });

                          // completedReviewerResponses[reviewerRelationship][[sectionKey]] = responses

                          if (!completedReviewerAnswersAverage[[sectionKey]]) {
                            completedReviewerAnswersAverage[[sectionKey]] = [];
                          }
                          // // console.log(sectionKey);
                          // // console.log(answers);
                          const answersMinusNegative = answers.filter((e) => e !== -1);

                          if (answersMinusNegative.length) {
                            completedReviewerAnswersAverage[[sectionKey]].push(average(answersMinusNegative));
                          } else {
                            completedReviewerAnswersAverage[[sectionKey]].push(0);
                          }

                          // Get all user Comments per-section (anonymously)
                          if (!completedReviewerComments[[sectionKey]]) {
                            completedReviewerComments[[sectionKey]] = [];
                          }
                          completedReviewerComments[[sectionKey]][[loopNum]] = comments[0];
                        });

                        if (loopNum == numberOfReviewers) {
                          // Convert all the reviews to averages.

                          let completedReviewerResponsesConvertedToAverages = {};
                          let completedReviewerResponsesConvertedToAveragesPercentage = {};

                          let completedReviewerResponsesSectionAverages = {};
                          let completedReviewerResponsesSectionAveragesNEW = {};
                          let allReviewerAnswers = {};

                          for (const i in completedReviewerResponsesAverage) {
                            for (const n in completedReviewerResponsesAverage[i]) {
                              if (completedReviewerResponsesAverage[i][n].length === 0) {
                                delete completedReviewerResponsesAverage[i][n];
                              }
                            }
                          }
                          // // console.log(completedReviewerResponsesAverage);
                          Object.keys(completedReviewerResponsesAverage).map((relationshipKey) => {
                            // loop through each key
                            completedReviewerResponsesConvertedToAverages[[relationshipKey]] = {};
                            completedReviewerResponsesConvertedToAveragesPercentage[[relationshipKey]] = {};

                            Object.keys(completedReviewerResponsesAverage[relationshipKey]).map((sectionKey) => {
                              if (completedReviewerResponsesAverage[relationshipKey][sectionKey].length > 0) {
                                completedReviewerResponsesConvertedToAverages[relationshipKey][[sectionKey]] = Number(
                                  average(completedReviewerResponsesAverage[relationshipKey][sectionKey]).toFixed(1)
                                );
                                completedReviewerResponsesConvertedToAveragesPercentage[relationshipKey][[sectionKey]] = Number(
                                  average(completedReviewerResponsesAverage[relationshipKey][sectionKey]).toFixed(1) / 5
                                );

                                // :|
                                if (!completedReviewerResponsesSectionAverages[[sectionKey]]) {
                                  completedReviewerResponsesSectionAverages[[sectionKey]] = [];
                                }

                                completedReviewerResponsesSectionAverages[[sectionKey]].push(...completedReviewerResponsesAverage[relationshipKey][sectionKey]);

                                if (!allReviewerAnswers[[sectionKey]]) {
                                  allReviewerAnswers[[sectionKey]] = [];
                                }

                                allReviewerAnswers[[sectionKey]].push(...completedReviewerResponsesAverage[relationshipKey][sectionKey]);

                                // }
                              }
                            });
                          });

                          // GET CORRECT SECTION AVERAGES!
                          Object.keys(completedReviewerResponsesSectionAverages).map((key, index) => {
                            let completedAverage = average(completedReviewerResponsesSectionAverages[key]).toFixed(2);
                            completedReviewerResponsesSectionAverages[key] = completedAverage;
                          });

                          let averageOfRelationshipAverages = {};
                          let questionAverageByRelationship = {};
                          Object.keys(completedReviewerAnswersByRelationship).map((answerKey) => {
                            Object.keys(completedReviewerAnswersByRelationship[answerKey]).map((relationshipKey) => {
                              if (!averageOfRelationshipAverages[answerKey] && !freeTextQuestionIDs.includes(answerKey)) {
                                averageOfRelationshipAverages[answerKey] = [];
                              }
                              if (!Object.keys(questionAverageByRelationship).includes(relationshipKey) && !freeTextQuestionIDs.includes(answerKey)) {
                                questionAverageByRelationship[relationshipKey] = {};
                              }
                              if (
                                !Object.keys(questionAverageByRelationship[relationshipKey]).includes(answerKey) &&
                                !freeTextQuestionIDs.includes(answerKey)
                              ) {
                                questionAverageByRelationship[relationshipKey][answerKey] = [];
                              }

                              const completedReviewerAnswersByRelationshipMinusZero = completedReviewerAnswersByRelationship[answerKey][relationshipKey].filter(
                                (e) => e !== 0
                              );
                              if (completedReviewerAnswersByRelationshipMinusZero.length) {
                                completedReviewerAnswersByRelationshipMinusZero.forEach((val, count) => {
                                  averageOfRelationshipAverages[answerKey].push(completedReviewerAnswersByRelationshipMinusZero[count]);
                                })
                                questionAverageByRelationship[relationshipKey][answerKey].push(
                                  Number(average(completedReviewerAnswersByRelationshipMinusZero).toFixed(2))
                                );
                              }
                            });
                          });
                          let mergedAverageOfAnswersByRelationship = {};
                          Object.keys(averageOfRelationshipAverages).map((answerKey) => {
                            if (!mergedAverageOfAnswersByRelationship[answerKey]) {
                              mergedAverageOfAnswersByRelationship[answerKey] = [];
                            }
                            mergedAverageOfAnswersByRelationship[answerKey].push(parseFloat(average(averageOfRelationshipAverages[answerKey])).toFixed(2));
                          });

                          let calculatedAverage = {};

                          Object.keys(mergedAverageOfAnswersByRelationship).map((key, index) => {
                            let completedAverage = mergedAverageOfAnswersByRelationship[key];
                            mergedAverageOfAnswersByRelationship[key] = completedAverage;
                            calculatedAverage[[key]] = completedAverage;

                            let descrepency = difference(userIndividualAnswers[key], mergedAverageOfAnswersByRelationship[key]).toFixed(2);
                            if (valueCheck(userIndividualAnswers[key].toFixed(1)) !== 'N/A' && valueCheck(parseFloat(mergedAverageOfAnswersByRelationship[key]).toFixed(1)) !== 'N/A') {
                              completedIndividualQuestionsDifferences[key] = descrepency;
                            }
                          });

                          let sectionAverageByRelationship = {};
                          for (const relKey in current360Relationships) {
                            sectionAverageByRelationship[relKey] = {};
                            for (const secID in sectionDetails) {
                              sectionAverageByRelationship[relKey][secID] = {};
                              if (Object.keys(completedReviewerResponsesAverage[relKey]).includes(secID)) {
                                sectionAverageByRelationship[relKey][secID] = average(completedReviewerResponsesAverage[relKey][secID]).toFixed(2);
                              }
                            }
                          }

                          let generateGraph = false;
                          let lCount = 0;
                          Object.keys(questionDetails).map((QID, l) => {
                            if (questionDetails[QID].answerType === 'MultiChoice') {
                              lCount++;
                            }
                          });
                          if (lCount > 0) {
                            generateGraph = true;
                          }

                          this.getPreviousScores(questionnaireID, questionnaireSections, sectionDetails, freeTextQuestionIDs);

                          this.setState({
                            userAnswers: userAnswers,
                            userResponses: userResponses,
                            userCommentsBySection: userCommentsBySection,
                            completedReviewerComments: completedReviewerComments,
                            userIndividualAnswers: userIndividualAnswers,
                            reviewerAnswers: completedReviewerAnswers,
                            reviewerResponses: completedReviewerResponses,
                            reviewerResponsesAverage: completedReviewerResponsesAverage,
                            reviewerResponsesConvertedToAverages: completedReviewerResponsesConvertedToAverages,
                            reviewerResponsesConvertedToAveragesPercentage: completedReviewerResponsesConvertedToAveragesPercentage,
                            freeTextQuestionIDs: freeTextQuestionIDs,
                            reviewerAnswersAverage: completedReviewerResponsesSectionAverages,
                            averageOfRelationshipAverages: averageOfRelationshipAverages,
                            mergedAverageOfAnswersByRelationship: mergedAverageOfAnswersByRelationship,
                            completedReviewerIndividualQuestionsAverage: calculatedAverage,
                            completedReviewerAnswersByRelationship: completedReviewerAnswersByRelationship,
                            completedReviewerResponsesByRelationship: completedReviewerResponsesByRelationship,
                            completedIndividualQuestionsDifferences: completedIndividualQuestionsDifferences,
                            completedReviewerCommentsBySection: completedReviewerCommentsBySection,
                            questionAverageByRelationship: questionAverageByRelationship,
                            generateGraph: generateGraph,
                            captions: captions,
                            descriptions: descriptions,
                            questionDetails: questionDetails,
                            freeTextQuestionDetails,
                            current360Title: current360Title,
                            sectionDetails: sectionDetails,
                            userFullName: userFullName,
                            reportPdfFileName: reportPdfFileName,
                            current360Relationships: current360Relationships,
                            sectionAverageByRelationship: sectionAverageByRelationship,
                            reviewerAnswersCountBySection: completedReviewerResponses,
                            sectionAnswersAverage,
                            orderedRelationshipKeys,
                            sectionsLegacyMode,
                            ...radarCaptionsData,
                            relationshipKeys,
                          });
                        }
                      });
                    });
                  } else {
                    this.setState({
                      userAnswers: userAnswers,
                      captions: captions,
                      descriptions: descriptions,
                      loading: false,
                    });
                  }
                });
              });
            });
          });
        });
      }
    });
    // .catch((data) => {
    //   this.setState({
    //     error: true
    //   })
    // })
  }

  getPreviousScores(questionnaireID, questionnaireSections, sectionDetails, freeTextQuestionIDs) {
    const accountID = this.props.match.params.accountId;
    const userID = this.props.match.params.userId;
    const previous360ID = this.props.current360.duplicatedFrom;
    const previousGroupId = this.props.match.params.previousGroupId;
    let previous360Scores = {};

    //get previous user scores
    db.doGetUserAnswersBySections(accountID, userID, previous360ID, previousGroupId, questionnaireID, 'self-review', questionnaireSections)
      .then((res) => {
        const userAnswers = res;
        previous360Scores['userAnswers'] = userAnswers;
        let userAverageCount = {};
        let userQuestionCount = {};
        let userAverage = {};
        let userAllAnswers = {};
        Object.keys(userAnswers).forEach((key) => {
          let blankSec = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          let sectionKey = key;
          let secAverage = [];
          userQuestionCount[sectionKey] = {};
          Object.keys(userAnswers[sectionKey].answers).forEach((key) => {
            let questKey = key;
            userAllAnswers[questKey] = parseInt(userAnswers[sectionKey].answers[questKey]);
            userQuestionCount[sectionKey][questKey] = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
            userQuestionCount[sectionKey][questKey][userAnswers[sectionKey].answers[questKey]]++;
            blankSec[userAnswers[sectionKey].answers[questKey]]++;
            if (parseInt(userAnswers[sectionKey].answers[questKey]) !== 0 && !freeTextQuestionIDs.includes(questKey)) {
              secAverage.push(parseInt(userAnswers[sectionKey].answers[questKey]));
            }
          });
          userAverageCount[sectionKey] = blankSec;
          userAverage[sectionKey] = average(secAverage);
        });
        previous360Scores['userAllAnswers'] = userAllAnswers;
        previous360Scores['userQuestionCount'] = userQuestionCount;
        previous360Scores['userSectionCount'] = userAverageCount;
        previous360Scores['userSectionAverage'] = userAverage;
        db.doGetCompletedReviewers(accountID, previous360ID, previousGroupId, userID).then((res) => {
          let reviewers = {};
          let averages = {};
          let reviewerAnswerCount = {};
          let reviewersByRelationship = {};
          Object.keys(this.state.current360Relationships).forEach((rel) => {
            reviewersByRelationship[rel] = [];
            averages[rel] = {};
            reviewerAnswerCount[rel] = {};
          });
          res.docs.forEach((doc) => ((reviewers[doc.id] = doc.data()), reviewersByRelationship[doc.data().relationship].push(doc.id)));

          let completedReviewerAnswers = {};

          Object.keys(this.state.current360Relationships).forEach((key) => {
            completedReviewerAnswers[key] = {};
          });

          let counter = 0;
          Object.keys(reviewers).forEach((key, index) => {
            let reviewerID = key;
            let loopNum = index + 1;

            db.doGetReviewerAnswers(accountID, reviewerID, questionnaireSections)
              .then((res) => {
                let reviewerAnswers = res;
                let holdQuestArr = {};
                let overallAverageByQuestion = {};
                Object.keys(reviewerAnswers).forEach((val, i) => {
                  const questionID = val;
                  Object.keys(reviewerAnswers[val].answers).forEach((val, i) => {
                    if (averages[reviewers[reviewerID].relationship][val] === undefined) {
                      if (!freeTextQuestionIDs.includes(val)) {
                        averages[reviewers[reviewerID].relationship][val] = [];
                      }
                      reviewerAnswerCount[reviewers[reviewerID].relationship][val] = [];
                    }
                    reviewerAnswerCount[reviewers[reviewerID].relationship][val].push(parseInt(reviewerAnswers[questionID].answers[val]));
                    if (parseInt(reviewerAnswers[questionID].answers[val]) !== 0 && !freeTextQuestionIDs.includes(val)) {
                      averages[reviewers[reviewerID].relationship][val].push(parseInt(reviewerAnswers[questionID].answers[val]));
                    }

                    //// console.log(reviewerAnswers[questionID].answers[val]);
                  });
                });

                let averageByQuestion = {};
                let averageBySections = {};
                let holding = {};

                Object.keys(averages).forEach((val, i) => {
                  let reviewerType = val;
                  averageByQuestion[reviewerType] = {};
                  averageBySections[reviewerType] = {};
                  Object.keys(averages[reviewerType]).forEach((val, n) => {
                    let questionID = val;
                    averageByQuestion[reviewerType][questionID] = average(averages[reviewerType][questionID]);
                  });
                  //push each section question to an array then average the array, then add the average to the object
                  let sectionArr;

                  Object.keys(sectionDetails).forEach((val, q) => {
                    sectionArr = [];
                    let secID = val;
                    // // console.log(questionnaireSections[secID].answers);
                    Object.keys(sectionDetails[secID].questions).forEach((key, n) => {
                      let questionID = key;

                      if (holding[questionID] === undefined) {
                        holding[questionID] = [];
                      }

                      if (Object.keys(averages[reviewerType]).length !== 0 && !freeTextQuestionIDs.includes(questionID)) {
                        // // console.log(questionID);
                        // // console.log(averages[reviewerType]);
                        averageBySections[reviewerType][secID] = 0;
                        averages[reviewerType][questionID].forEach((item) => {
                          sectionArr.push(item);
                          holding[questionID].push(item);
                        });
                      }
                    });

                    if (sectionArr.length !== 0) {
                      let secAverage = average(sectionArr).toFixed(2);
                      averageBySections[reviewerType][secID] = secAverage;
                    }
                  });
                });

                previous360Scores['reviewerAnswersPerQuestion'] = holding;
                previous360Scores['answersWithoutZeros'] = averages;
                previous360Scores['averageBySections'] = averageBySections;
                previous360Scores['averageByQuestion'] = averageByQuestion;

                //this makes sure that the loading state is set to false only after everything is ready
                if (index + 1 === Object.keys(reviewers).length) {
                  if (Object.keys(previous360Scores.averageByQuestion).length === Object.keys(this.state.current360Relationships).length) {
                    let questionAnswerCount = {};
                    let sectionAnswerCount = {};

                    Object.keys(reviewerAnswerCount).forEach((n) => {
                      //add the reviewer refs
                      questionAnswerCount[n] = {};
                      sectionAnswerCount[n] = {};

                      //this gets the count by question
                      Object.keys(sectionDetails).forEach((q) => {
                        //add the section IDs
                        questionAnswerCount[n][q] = {};
                        sectionAnswerCount[n][q] = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
                        Object.keys(sectionDetails[q].questions).forEach((p) => {
                          questionAnswerCount[n][q][p] = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
                          if (reviewerAnswerCount[n][p] !== undefined) {
                            Object.values(reviewerAnswerCount[n][p]).forEach((x) => {
                              questionAnswerCount[n][q][p][x]++;
                              sectionAnswerCount[n][q][x]++;
                            });
                          }
                        });
                      });
                    });
                    let averageScoresPerSec = {};
                    let scores = {};
                    Object.keys(averageBySections).forEach((key) => {
                      Object.keys(averageBySections[key]).forEach((n) => {
                        if (scores[n] === undefined) {
                          scores[n] = [];
                          averageScoresPerSec[n] = 0;
                        }
                        scores[n].push(parseFloat(averageBySections[key][n]));
                      });
                    });
                    Object.keys(averageScoresPerSec).forEach((key) => {
                      averageScoresPerSec[key] = parseFloat(average(scores[key])).toFixed(2);
                    });
                    //// console.log(averageScoresPerSec);

                    previous360Scores['reviewerAnswerCount'] = questionAnswerCount;
                    previous360Scores['sectionAnswerCount'] = sectionAnswerCount;
                    previous360Scores['averageScoresPerSec'] = averageScoresPerSec;
                    this.setState({ previous360Scores, loading: false });
                  }
                }
              })
              .catch((err) => {
                // console.log(err);
              });
          });
        });
      })
      .catch((err) => {
        // console.log(err);
      });
  }

  componentDidUpdate(prevProps, prevState) {
    // run initial updates for pdfRenderCompleted when the both the radar chart captions and detailed feedback sections are finished
    if (
        !this.state.pdfRenderCompleted &&
        (
          (prevState.competencyComparisonPDFRenderCompleted !== this.state.competencyComparisonPDFRenderCompleted) 
          || 
          (prevState.rankedCompetenciesPDFRenderCompleted !== this.state.rankedCompetenciesPDFRenderCompleted)
          ||
          (prevState.orderedStatementsPDFRenderCompleted !== this.state.orderedStatementsPDFRenderCompleted)
          ||
          (prevState.statementVariationsPDFRenderCompleted !== this.state.statementVariationsPDFRenderCompleted)
          ||
          (prevState.detailedFeedbackPDFRenderCompleted !== this.state.detailedFeedbackPDFRenderCompleted) 
        )
      ) {
        if (
          this.state.competencyComparisonPDFRenderCompleted 
          && this.state.rankedCompetenciesPDFRenderCompleted 
          && this.state.orderedStatementsPDFRenderCompleted
          && this.state.statementVariationsPDFRenderCompleted 
          && this.state.detailedFeedbackPDFRenderCompleted
        ) {
          this.setState({ layoutMode: 'PDFRender' })
        }
      } else if (this.state.layoutMode === 'PDFRender') {
        const saveReport = async () => {
          await this.report.save();
          this.setState({layoutMode: 'pdfCompleted'})
          return;
        }
        saveReport();
      }
  }
  updateCompetencyComparisonPDFExportPreRenderStatus() {
    this.setState({competencyComparisonPDFRenderCompleted: true})
  }
  updateRankedCompetenciesPDFExportPreRenderStatus() {
    this.setState({rankedCompetenciesPDFRenderCompleted: true})
  }
  updateOrderedStatementsPDFExportPreRenderStatus() {
    this.setState({orderedStatementsPDFRenderCompleted: true})
  }
  updateStatementVariationsPDFExportPreRenderStatus() {
    this.setState({statementVariationsPDFRenderCompleted: true})
  }
  updateDetailedFeedbackPDFExportPreRenderStatus() {
    this.setState({detailedFeedbackPDFRenderCompleted: true})
  }

  render() {
    const {generateGraph, relationshipKeys, current360Relationships, sectionsLegacyMode, sectionDetails, pdfRequested, radarSortedSectionIDs, captionKeysList, layoutMode} = this.state;
    if (!this.state.loading) {
      let emptyData = {
        data: {},
        meta: {},
      };
      let userData = this.state.userAnswers;
      let chartCaptions = this.state.captions;
      const data = [
        {
          data: {},
          meta: { color: 'blue' },
        },
      ];
      for (const q in this.state.sectionDetails) {
        let counter = 0;
        for (const p in this.state.sectionDetails[q].questions) {
          if (this.state.sectionDetails[q].questions[p].answerType === 'MultiChoice') {
            counter++;
          }
        }
        if (counter === 0) {
          delete userData[q];
          delete chartCaptions[q];
        }
      }
      const captionsLoaded = chartCaptions;
      data[0]['data'] = userData;

      if (this.state.reviewerResponsesConvertedToAveragesPercentage) {
        Object.keys(this.state.reviewerResponsesConvertedToAveragesPercentage).map((key, index) => {
          if (Object.keys(this.state.reviewerResponsesConvertedToAveragesPercentage[key]).length) {
            data.push({
              data: this.state.reviewerResponsesConvertedToAveragesPercentage[key],
              meta: {
                color: this.state.current360Relationships[key]['colour'],
              },
            });
          }
        });
      }

      const chartOptions = getChartOptions(captionsLoaded, radarSortedSectionIDs, 14);

      const nullLoop = { 0: 1, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

      let reviewerAnswersAverage = this.state.reviewerAnswersAverage;
      let mergedAverageOfAnswersByRelationship = this.state.mergedAverageOfAnswersByRelationship;
      let completedIndividualQuestionsDifferences = this.state.completedIndividualQuestionsDifferences;

      //check how many reviewer groups there are, if there are more than 4 then lower it
      //as there will be more feedback per row than can fit on the page with a pagesplit of 4
      let pageSplit = 4;
      let page3 = 7;
      let currentReviewTypes = Object.keys(this.state.reviewerAnswers).length;
      let prevReviewTypes = Object.keys(this.state.previous360Scores.answersWithoutZeros).length;
      if (currentReviewTypes > pageSplit || prevReviewTypes > pageSplit) {
        pageSplit = pageSplit - 2;
      }

      //is a 3rd table page going to be needed?

      Object.keys(this.state.sectionDetails).map((key) => {
        if (Object.keys(this.state.sectionDetails[key].questions).length > 6 && pageSplit == 2) {
          page3 = 7;
        }

        if (Object.keys(this.state.sectionDetails[key].questions).length > 7 && pageSplit == 4) {
          page3 = 8;
        }
      });

      const reviewerAnswersAverageKeys = Object.keys(reviewerAnswersAverage).sort(function(a, b) {
        return reviewerAnswersAverage[b] - reviewerAnswersAverage[a];
      })
      const reviewerAnswersAverageLength = reviewerAnswersAverageKeys.length
      const numberOfRankedCompetencyPages = Math.ceil(reviewerAnswersAverageLength / 4)
      const rankedCompetencyPages = arrayGenerator(numberOfRankedCompetencyPages)
      // revieweeAnswersAverage={this.state.userAnswers}
      let revieweeAnswersAverage = {};
      for (const sectionID in this.state.userAnswers) {
        revieweeAnswersAverage[sectionID] = +this.state.userAnswers[sectionID] * 5;
      }

      return (
        <div className='page-wrapper page-wrapper--report'>
          <section className='container container--slim'>
            <Breadcrumbs
              current360={this.props.current360}
              accountData={this.props.accountData}
              current360Groups={this.props.current360Groups}
              current360Relationships={this.props.current360Relationships}
              links={[
                {
                  title: 'Dashboard',
                  to: `/dashboard/${this.props.match.params.accountId}`,
                },
                {
                  title: `${this.props.accountData.accountTitle ? this.props.accountData.accountTitle : this.props.accountData.title} ${
                    this.props.current360.type
                  } Projects`,
                  to: `/dashboard/${this.props.match.params.accountId}/${this.props.current360.type === 'Goal Tracker' ? 'goal-tracker' : '360'}-projects/`,
                },
                {
                  title: this.props.current360.title,
                  to: '../../../edit/',
                },
                {
                  title: this.state.current360Groups[this.props.match.params.groupId].title,
                  to: `../../../edit/group/${this.props.match.params.groupId}/`,
                },
                {
                  title: `Comparison Report`,
                  to: null,
                },
              ]}
            />
          </section>
          <div
            className='user-report-view-only'
            style={{ height: '100vh', width: '100vw', paddingTop: 20, paddingBottom: 100, backgroundColor: 'rgb(82, 86, 89)', overflow: 'hidden' }}
          >
            <div className='tc' style={{ marginBottom: '1.4em' }}>
              <Link className='btn btn--back btn--white ml2 mr3' to={`../../../edit/group/${this.state.groupId}/`}>
                Back to group overview
              </Link>
              <PDFExportButton pdfRequested={pdfRequested} layoutMode={layoutMode} exportFunction={this.exportPDF} />
            </div>
            <div
              style={{
                maxWidth: '210mm',
                height: '90vh',
                backgroundColor: 'white',
                boxShadow: '0px 0px 5px #000',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: 100,
                padding: '0px',
                overflowX: 'scroll',
                overflowY: 'scroll',
              }}
            >
              <PDFExport
                paperSize={'A4'}
                forcePageBreak='.page-break'
                keepTogether={'div'}
                margin={{ bottom: 50 }}
                pageTemplate={ReportPageTemplate}
                fileName={this.state.reportPdfFileName}
                title=''
                subject=''
                keywords=''
                ref={(r) => (this.report = r)}
              >
                <div
                  className='user-report'
                  style={{
                    height: '100%',
                    width: '100%',
                    padding: '0px',
                  }}
                >
                  <header className='user-report__header'>
                    <img crossOrigin='anonymous' src={this.props.current360.settings.logo} className='header-logo-img' />
                    <div className='w-80'>
                      <h1 className='user-report__header-title'>{this.state.current360Title}</h1>
                      <p className='user-report__header-sub-title'>
                        <strong>Report & Development plan</strong>
                      </p>
                      <p className='user-report__header-name'>For {this.state.userFullName}</p>
                    </div>
                  </header>
                  <div className='user-report__contents'>
                    <h1>Contents</h1>
                    <hr />
                    <h2 className='f3 mv4 mb3'>
                      <strong>Instructions</strong>
                    </h2>
                    <hr />
                    <h2 className='f3 mt4 mb0'>
                      <strong>Part 1</strong>
                    </h2>
                    <h4 className='f4 mb4 mt0'>Your Feedback Summary</h4>
                    <hr />
                    <h2 className='f3 mt4 mb0'>
                      <strong>Part 2</strong>
                    </h2>
                    <h4 className='f4 mb4 mt0'>Your detailed feedback</h4>
                    <hr />
                    <h2 className='f3 mt4 mb0'>
                      <strong>Part 3</strong>
                    </h2>
                    <h4 className='f4 mb4 mt0'>Handling feedback</h4>
                    <hr />
                    <h2 className='f3 mt4 mb0'>
                      <strong>Part 4</strong>
                    </h2>
                    <h4 className='f4 mb4 mt0'>Your development plan</h4>
                  </div>
                  <div className='title-page page-break'>
                    <div className='w-60'>
                      <h2 className='f2 mv0'>Introduction</h2>
                    </div>
                  </div>
                  <div className='bg-white user-report__content'>
                    <h2 className='f2 mv0 mt4'>
                      <strong>Introduction</strong>
                    </h2>
                    <h3 className='f5 gray'>Getting the most from your feedback</h3>
                    <hr className='' />

                    <h3 className='f5 mt4'>
                      <strong>Where to begin?</strong>
                    </h3>
                    <p>
                      It’s a good idea to read the report in the order provided. However, you may prefer to dive into a specific section and then pull out
                      related information from other parts of the report. You can see how the different elements of the report relate to each other, in the
                      section ‘How your report is compiled’.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Look for consistent messages</strong>
                    </h3>
                    <p>
                      The key to understanding your feedback is to look for clear and consistent themes across your report. Look for competencies and statements
                      where you, and those providing feedback have given similar ratings or comments. These consistent themes will provide you with an
                      indication of where your strengths, or opportunities for improvements, can be found.
                    </p>
                    <p>
                      Similarly, you may notice that some rater groups (groups of reviewers; e.g., ‘direct reports’) recognise strengths/opportunities in
                      specific competencies and statements more frequently than others. In these instances, you should consider why, when, and how this might
                      occur. Ask yourself: what is one rater group seeing/not seeing that the others are/are not?
                    </p>
                    <p>
                      You should interpret one-off or isolated ratings or comments carefully. If you do find these, consider the context in which they may have
                      been provided. Ask yourself if there is any truth in these findings, or if they can be classified as ‘outliers’?
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Make notes</strong>
                    </h3>
                    <p>
                      There are coaching questions throughout the report, these are provided to encourage you to reflect on your feedback. Additionally, the
                      report provides space for you to capture notes, as you read through.
                    </p>

                    <h3 className='f5 mt4 page-break'>
                      <strong>Build your development plan</strong>
                    </h3>
                    <p>
                      All your feedback will help you to learn, develop and grow. Take the time to consider how you can use the feedback to help shape your
                      future development. What is your report telling you about your opportunities to develop? Reflect on how you can put this feedback into
                      action, by fleshing out your development plan with practical and positive actions and activities.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Don’t be afraid to ask for help</strong>
                    </h3>
                    <p>
                      Asking for help is a positive step! If you are unsure about some of the feedback you have received or feel uncertain about how to respond
                      to the feedback positively; talk to a trusted colleague, manager, coach or colleague within HR/Learning. Book some time to sit down and
                      discuss your concerns and ask them for help interpreting the feedback. If needed, they can help you to build some effective developmental
                      solutions in response to the feedback.
                    </p>

                    <h2 className='f2 mv0 mt4 page-break'>
                      <strong>Introduction</strong>
                    </h2>
                    <h3 className='f5 gray'>How your report is compiled</h3>
                    <hr className='' />

                    <h3 className='f5 mt4'>
                      <strong>Two things to note</strong>
                    </h3>
                    <p>Your feedback is made up of two key elements: competencies and statements.</p>

                    <h3 className='f5 mt4'>
                      <strong>Competencies</strong>
                    </h3>
                    <p>Competencies are high-level collections/groups of skills or knowledge. Each competency is made up of a series of statements.</p>

                    <h3 className='f5 mt4'>
                      <strong>Statements</strong>
                    </h3>
                    <p>Statements are a series of actions or behaviours, which are used to describe each competency area.</p>

                    <h3 className='f5 mt4'>
                      <strong>Rater Groups</strong>
                    </h3>
                    <p>
                      Rater groups are also known as ‘relationship groups’ or ‘reviewer groups’. They are the collection of reviewers who have provided you with
                      feedback, enabling this report to be produced. The language used to describe these groups varies, depending on your organisation’s
                      preferences. As a norm, you can expect the following terms to be used:
                    </p>

                    <p>
                      &bull; Self / Own (your feedback)
                      <br />
                      &bull; Direct Reports / Reports / Team (those people that report into you)
                      <br />
                      &bull; Line Manager / Higher / Manager / Senior (those people that you report in to)
                      <br />
                      &bull; Peers / Same (people who operate at the same or a similar level to you in the organisation)
                      <br />
                      &bull; Other / External (people who work externally to the organisation; e.g., suppliers)
                      <br />
                    </p>

                    <h3 className='f5 mt4 page-break'>
                      <strong>Rating calculations</strong>
                    </h3>
                    <p>
                      When completing the online questionnaire, you and your colleagues rated each statement against a rating scale. These ratings are collated
                      and averaged to show the mean rating for each statement, and for each of the overall competencies.
                    </p>
                    <p>
                      Your self-review ratings are shown separately from those of your colleagues. Self-review ratings are not included in the average ratings.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Unable to comment rating</strong>
                    </h3>
                    <p>
                      There are occasions where some of your reviewers may feel unable to provide you with feedback to a specific statement. In these instances,
                      they might have selected the ‘unable to comment’ rating.{' '}
                    </p>
                    <p>
                      Whenever you, or your reviewer, choose this rating, it is not included in the rating calculation for that statement, or in the overall
                      competency rating.
                    </p>
                  </div>

                  {/* part 1 step 1 */}
                  <RelationshipCompetencyComparison
                    reportType='Comparison'
                    generateGraph={generateGraph}
                    completedRelationshipIDs={relationshipKeys}
                    current360Relationships={current360Relationships}
                    chartOptions={chartOptions}
                    captionsLoaded={captionsLoaded}
                    data={data}
                    convertSVGToImage={this.convertSVGToImage}
                    orderedSectionIDs={radarSortedSectionIDs}
                    numberOfSections={radarSortedSectionIDs.length}
                    pdfRequested={pdfRequested}
                    sectionDetails={sectionDetails}
                    captionKeysList={captionKeysList}
                    updateCompetencyComparisonPDFExportPreRenderStatus={this.updateCompetencyComparisonPDFExportPreRenderStatus}
                    layoutMode={layoutMode}
                  />

                  <RankedCompetencies
                    reportType='Comparison'
                    generateGraph={generateGraph}
                    reviewerAnswersAverage={reviewerAnswersAverage}
                    previousReviewerAverages={this.state.previous360Scores.averageScoresPerSec}
                    answerKeys={Object.keys(reviewerAnswersAverage)}
                    sectionDetails={sectionDetails}
                    revieweeAnswersAverage={revieweeAnswersAverage}
                    previousRevieweeAverages={this.state.previous360Scores.userSectionAverage}
                    updateRankedCompetenciesPDFExportPreRenderStatus={this.updateRankedCompetenciesPDFExportPreRenderStatus}
                    pdfRequested={pdfRequested}
                    layoutMode={layoutMode}
                  />

                  <OrderedStatements
                    reportType='Comparison'
                    generateGraph={generateGraph}
                    reviewerAnswersAverages={mergedAverageOfAnswersByRelationship}
                    previousReviewerAverages={this.state.previous360Scores.reviewerAnswersPerQuestion}
                    questionDetails={this.state.questionDetails}
                    userIndividualAnswers={this.state.userIndividualAnswers}
                    previousRevieweeAverages={this.state.previous360Scores.userAllAnswers}
                    updateOrderedStatementsPDFExportPreRenderStatus={this.updateOrderedStatementsPDFExportPreRenderStatus}
                    pdfRequested={pdfRequested}
                    layoutMode={layoutMode}
                  />

                  <StatementVariations 
                    reportType='Comparison'
                    generateGraph={generateGraph}
                    scoreDifferences={completedIndividualQuestionsDifferences}
                    mergedAverageOfAnswersByRelationship={mergedAverageOfAnswersByRelationship}
                    userIndividualAnswers={this.state.userIndividualAnswers}
                    questionDetails={this.state.questionDetails}
                    updateStatementVariationsPDFExportPreRenderStatus={this.updateStatementVariationsPDFExportPreRenderStatus}
                    pdfRequested={pdfRequested}
                    layoutMode={layoutMode}
                  />

                  <DetailedFeedbackSection
                    loading={this.state.loading}
                    reportType='Comparison'
                    sectionDetails={this.state.sectionDetails}
                    questionDetails={this.state.questionDetails}
                    freeTextQuestionDetails={this.state.freeTextQuestionDetails}
                    sectionsLegacyMode={this.state.sectionsLegacyMode}
                    secLengthNoFreeText={this.state.secLengthNoFreeText}
                    sectionAverageSelfCurrent={this.state.sectionAnswersAverage}
                    sectionResponsesSelfCurrent={this.state.userResponses}
                    previous360Scores={this.state.previous360Scores}
                    current360Relationships={this.state.current360Relationships}
                    sectionAveragesByRelationshipCurrent={this.state.sectionAverageByRelationship}
                    sectionResponsesByRelationshipCurrent={this.state.reviewerAnswersCountBySection}
                    questionAveragesSelfCurrent={this.state.userIndividualAnswers}
                    questionAveragesByRelationshipCurrent={this.state.questionAverageByRelationship}
                    questionResponsesByRelationshipCurrent={this.state.completedReviewerAnswersByRelationship}
                    userFreetextResponses={this.state.userCommentsBySection}
                    reviewerFreetextResponses={this.state.completedReviewerCommentsBySection}
                    dfSectionRelationshipKeys={this.state.orderedRelationshipKeys}
                    relationshipKeys={this.state.relationshipKeys}
                    pdfRequested={this.state.pdfRequested}
                    updateDetailedFeedbackPDFExportPreRenderStatus={this.updateDetailedFeedbackPDFExportPreRenderStatus}
                    layoutMode={layoutMode}
                  />

                  <div className='title-page page-break'>
                    <div className='w-60'>
                      <h1 className='f3 mv0 mb3 light-blue'>Part 3:</h1>
                      <h2 className='f2 mv0'>Handling Feedback</h2>
                    </div>
                  </div>

                  <div className='bg-white user-report__content'>
                    <h2 className='f2 mv0 mt4'>
                      Part 3: <strong>Handling Feedback</strong>
                    </h2>
                    <h3 className='f5 gray'>Responding to feedback</h3>
                    <hr className='' />

                    <h3 className='f5 mt4'>
                      <strong>Take some time</strong>
                    </h3>
                    <p>
                      Receiving feedback can be challenging. Even if the input is praise, you may have difficulty in knowing how to respond to it, or you may
                      feel embarrassed by it. When the feedback is constructive, emotional and psychological reactions can be complicated and can get in the way
                      of seeing where the real value lies in the information. Take some time to reflect and absorb the feedback before you consider your
                      response.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Receiving praise</strong>
                    </h3>
                    <p>Receiving praise is not as easy as it may seem.</p>
                    <p>
                      When people are given a compliment, whether professionally or personally, there are several ways they may react that reduces the impact of
                      the praise:
                    </p>
                    <p>
                      &bull; Devaluation: "It’s just me doing my job."
                      <br />
                      &bull; Distrust: "People are just saying that; they are just being nice."
                      <br />
                      &bull; Deflection: "My team does all the hard work for me."
                    </p>
                    <p>
                      All of these are common responses, but it is crucial to understand why you may use them. If these are normal reactions for you, consider
                      why you feel the need to use them.
                    </p>
                    <p>
                      Sometimes, self-critical people can find it awkward to accept praise, as it does not fit with their view of themselves. In some cases,
                      they set their personal bar too high for themselves, or anyone else, to reach realistically. If this sounds like you, make it a
                      developmental target to try to persuade yourself that perfection is not always necessary, to perform well in your job and your life.
                    </p>
                    <p>
                      Accepting a genuine compliment and positive feedback gracefully can be both modest and assertive. The best way to take positive feedback
                      is to thank the provider, genuinely and happily. They gave you the feedback for a reason, and they will most likely be happy to see it has
                      the desired effect.
                    </p>
                  </div>
                  <div className='page-break bg-white user-report__content'>
                    <h3 className='f5 mt4'>
                      <strong>Be open-minded</strong>
                    </h3>
                    <p>
                      Open your mind up to the potential for change. Remember that constructive feedback is not about you as a person, but about job
                      behaviours/actions. Because you have not come up to a necessary standard in some way, does not make you a bad person or a failure—it
                      certainly does not mean the person offering the feedback is suggesting you are. It merely means you have the scope to learn and improve.
                      The person providing feedback should be focused on helping you to do this, so try to see the input from that perspective.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Contextualise, analyse and confirm</strong>
                    </h3>
                    <p>
                      Remember that you have the right to analyse and then confirm the feedback you received for accuracy. Consider the context in which the
                      feedback was provided, to help support your thinking.
                    </p>
                    <p>
                      If you are uncertain about some of the feedback you have received, or feel unsure about how to respond to the feedback positively; talk to
                      a trusted colleague, manager, coach or colleague within HR/Learning. Book some time to sit down and discuss your concerns and ask them for
                      help interpreting the feedback. If needed, they can help you to build some effective developmental solutions in response to the feedback.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Move forward</strong>
                    </h3>
                    <p>
                      With any feedback, the critical response is to consider what you can do to improve in future. Can the people giving you feedback help?
                      Don’t be afraid to ask them for suggestions and assistance in making the necessary changes. All being well, they will be happy to do this.
                      Work with them to develop a plan of action.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Build your developmental plan</strong>
                    </h3>
                    <p>
                      All your feedback is intended to help you learn, develop and grow. Take the time to consider how you can use the feedback to help shape
                      your future development. What is your report telling you about your opportunities to develop? Reflect on how you can put this feedback
                      into action, by fleshing out your development plan with practical and positive actions and activities.
                    </p>
                  </div>
                  <div className='page-break bg-white user-report__content'>
                    <h2 className='f2 mv0'>
                      Part 3: <strong>Handling Feedback</strong>
                    </h2>
                    <h3 className='f5 gray'>Handling critical feedback</h3>
                    <hr className='' />

                    <p>
                      Receiving critical feedback about yourself from other people can sometimes result in strong emotional reactions, particularly where your
                      sense of who you are, or your self-concept, has been challenged.
                    </p>
                    <p>This is quite normal and may lead to some of the following emotions and behaviours being exhibited:</p>

                    <h3 className='f5 mt4'>
                      <strong>Shock</strong>
                    </h3>
                    <p>
                      When receiving feedback that people do not recognise as being part of who they are, they may consciously or unconsciously demonstrate
                      disbelief and a sense of shock. Typically, this is often followed by denial and a refusal to accept the facts or feedback. This is a
                      defence mechanism and is perfectly natural, but it can take an individual some time to overcome it.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Annoyance</strong>
                    </h3>
                    <p>
                      Annoyance manifests itself in various ways. People may be upset with themselves, but more often, it is directed at external parties,
                      including the people who provided the feedback or the person delivering the message.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Refusal</strong>
                    </h3>
                    <p>
                      In the refusal stage people may begin to be less annoyed, but still resist the message by challenging the validity of the process, or by
                      starting to make excuses.
                    </p>
                    <p>
                      Following this stage, people may appear to have accepted the message, by declaring that 'I understand what you are saying', but without
                      really meaning it. This may, in turn, lead to objections being raised further down the line, with the key stakeholders in the feedback
                      process.
                    </p>

                    <h3 className='f5 mt4'>
                      <strong>Acceptance</strong>
                    </h3>
                    <p>
                      Having had time to evaluate the information objectively and to come to terms with what people have said, acceptance usually occurs. This
                      stage represents an excellent opportunity for an individual to get some coaching or training to enhance their strengths or to address some
                      areas of development.
                    </p>
                  </div>

                  <div className='title-page page-break'>
                    <div className='w-60'>
                      <h1 className='f3 mv0 mb3 light-blue'>Part 4:</h1>
                      <h2 className='f2 mv0'>Your development plan</h2>
                    </div>
                  </div>

                  <div className='bg-white user-report__content'>
                    <h2 className='f2 mv0 mt4'>Your Development Plan</h2>
                    <h3 className='f5 gray'>Guide</h3>
                    <hr className='' />

                    <p>You need to create a development plan, with specific goals, to get the most out of your feedback.</p>
                    <p>There are three stages to creating an effective development plan:</p>
                    <p>
                      &bull; Reviewing your 360 feedback
                      <br />
                      &bull; Creating some SMART goals
                      <br />
                      &bull; Reviewing and tracking your progress
                    </p>
                    <p>If you follow these stages, particularly the regular review of your progress, you will get the most value from your feedback.</p>
                  </div>

                  <div className='page-break bg-white user-report__content'>
                    <h2 className='f2 mv0'>
                      Part 4: <strong>Step 1</strong>
                    </h2>
                    <h3 className='f5 gray'>Review your feedback</h3>
                    <hr className='' />

                    <p>Go back through the notes, reflections and observations that you have made throughout your report.</p>
                    <p>
                      Use these to build a summary of the key messages that stand out from the feedback. This summary will help you to focus your thinking when
                      it comes to creating some relevant goals.
                    </p>

                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                  </div>

                  <div className='page-break bg-white user-report__content'>
                    <h2 className='f2 mv0'>
                      Part 4: <strong>Step 2</strong>
                    </h2>
                    <h3 className='f5 gray'>My goals</h3>
                    <hr className='' />

                    <p>
                      Use your summary findings to guide you through setting a small number of developmental goals that are stretching but achievable. The
                      statements in the report can form a sound basis for your goals. We recommend that you put some dates into your diary, to review your
                      progress on your developmental goals.
                    </p>
                  </div>

                  <div className='page-break bg-white user-report__content'>
                    <h2 className='f2 mv0'>
                      Part 4: <strong>Step 2</strong>
                    </h2>
                    <h3 className='f5 gray'>Goal 1</h3>
                    <hr className='' />

                    <p>My goal is:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>Specific actions needed to deliver my goal:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>I will measure my success by:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>The support I need:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>When will I review my progress?</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>My deadline is:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                  </div>

                  <div className='page-break bg-white user-report__content'>
                    <h2 className='f2 mv0'>
                      Part 4: <strong>Step 2</strong>
                    </h2>
                    <h3 className='f5 gray'>Goal 2</h3>
                    <hr className='' />

                    <p>My goal is:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>Specific actions needed to deliver my goal:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>I will measure my success by:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>The support I need:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>When will I review my progress?</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>My deadline is:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                  </div>

                  <div className='page-break bg-white user-report__content'>
                    <h2 className='f2 mv0'>
                      Part 4: <strong>Step 2</strong>
                    </h2>
                    <h3 className='f5 gray'>Goal 3</h3>
                    <hr className='' />

                    <p>My goal is:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>Specific actions needed to deliver my goal:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>I will measure my success by:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>The support I need:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>When will I review my progress?</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>

                    <p>My deadline is:</p>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                    <div className='w-100 bb bl-0 br-0 bt-0 b--dashed b--light-silver pv2 mb1'></div>
                  </div>

                  <header className='page-break user-report__header'>
                    <img crossOrigin='anonymous' src={this.props.current360.settings.logo} className='header-logo-img' />
                    <div className='w-80'>
                      <h1 className='user-report__header-title'>Good Luck</h1>
                      <p className='user-report__header-sub-title'>
                        <strong>Own the change you want to see! </strong>
                      </p>
                    </div>
                  </header>
                </div>
              </PDFExport>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          {this.state.error ? (
            <div className='error-404 bg-blue fixed left-0 top-0'>
              <h1 className='white mt4'>System Error - Error loading report</h1>
            </div>
          ) : (
            <LoadingState />
          )}
        </div>
      );
    }
  }
}

export default withAuthorisation(authCondition)(UserComparisonReport);
